/**
 * LocationPicker — Leaflet + OpenStreetMap (no API key, free).
 *
 * Props:
 *  - value: "lat,lng" string atau null
 *  - onChange: (newCoord: "lat,lng") => void
 *  - height?: number (default 280)
 *  - center?: {lat, lng} (default Jakarta)
 *
 * UX:
 *  - Pin draggable
 *  - Klik di map → pin pindah
 *  - "Cari Lokasi Saya" → geolocation API
 *  - Display coordinate string lengkap
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, Crosshair, Search, Loader2 } from "lucide-react";
import { toast } from "sonner";

// Fix Leaflet default icon paths (webpack lazy-load doesn't bundle icon images)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

const DEFAULT_CENTER = { lat: -6.2, lng: 106.816666 }; // Jakarta

function parseCoord(value) {
  if (!value || typeof value !== "string") return null;
  // Accept "lat,lng" or "(lat,lng)"
  const m = value.replace(/[()]/g, "").trim().match(/^(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)$/);
  if (!m) return null;
  const lat = Number(m[1]);
  const lng = Number(m[2]);
  if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
  return { lat, lng };
}

function formatCoord({ lat, lng }) {
  return `${lat.toFixed(7)},${lng.toFixed(7)}`;
}

function ClickHandler({ onPick }) {
  useMapEvents({
    click(e) {
      onPick({ lat: e.latlng.lat, lng: e.latlng.lng });
    },
  });
  return null;
}

function CenterControl({ position, zoomHint }) {
  const map = useMap();
  useEffect(() => {
    if (!position) return;
    // zoomHint dipakai saat user pilih hasil search → fokus dekat (16).
    // Selain itu, jaga zoom user kecuali masih terlalu jauh.
    const target = zoomHint || (map.getZoom() < 13 ? 14 : map.getZoom());
    map.flyTo([position.lat, position.lng], target);
  }, [position?.lat, position?.lng, zoomHint]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

export default function LocationPicker({ value, onChange, height = 280, center, showCoords = false }) {
  const initialPos = useMemo(() => parseCoord(value) || center || DEFAULT_CENTER, [value, center]);
  const [pos, setPos] = useState(initialPos);
  const markerRef = useRef(null);

  // Address search (Nominatim) — no API key needed, ToS friendly: ≤1 req/sec,
  // include UA, country=ID for relevance.
  const [searchQuery, setSearchQuery] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);  // array | null = idle, [] = no results
  const [searchError, setSearchError] = useState("");
  const [searchTouched, setSearchTouched] = useState(false);
  // bumped tiap kali user pilih hasil search → trigger zoom 16 di CenterControl
  const [searchZoomTick, setSearchZoomTick] = useState(0);

  // Keep `pos` in sync with external `value` changes (e.g., parent reset)
  useEffect(() => {
    const next = parseCoord(value);
    if (next && (next.lat !== pos.lat || next.lng !== pos.lng)) {
      setPos(next);
    }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const updatePos = (next) => {
    setPos(next);
    onChange?.(formatCoord(next));
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Browser tidak mendukung geolocation");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (p) => updatePos({ lat: p.coords.latitude, lng: p.coords.longitude }),
      (err) => toast.error(`Gagal ambil lokasi: ${err.message}`),
      { enableHighAccuracy: true, timeout: 8000 },
    );
  };

  // Cari alamat via Nominatim (OpenStreetMap) — gratis, tanpa API key.
  const searchAddress = async () => {
    const q = searchQuery.trim();
    if (!q) return;
    setSearchTouched(true);
    setSearchLoading(true);
    setSearchError("");
    setSearchResults(null);
    try {
      const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q)}&limit=5&countrycodes=id&addressdetails=1`;
      const res = await fetch(url, {
        headers: { Accept: "application/json" },
      });
      if (!res.ok) throw new Error(`http_${res.status}`);
      const data = await res.json();
      const items = Array.isArray(data) ? data : [];
      setSearchResults(items);
    } catch (err) {
      setSearchError("Gagal mencari alamat. Silakan coba lagi.");
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  // Saat user pilih hasil → pindahkan marker, center map dengan zoom 16.
  const selectResult = (item) => {
    const lat = Number(item.lat);
    const lon = Number(item.lon);
    if (Number.isNaN(lat) || Number.isNaN(lon)) return;
    updatePos({ lat, lng: lon });
    setSearchZoomTick((t) => t + 1);
    setSearchResults(null);
    setSearchTouched(false);
  };

  return (
    <div className="space-y-2" data-testid="location-picker">
      {/* Address search bar — Nominatim (free, no API key) */}
      <div className="relative" data-testid="picker-search-wrap">
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); searchAddress(); } }}
              placeholder="Cari alamat / nama tempat..."
              className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-[#E5E5E5] bg-white focus:outline-none focus:brand-border-primary"
              data-testid="picker-search-input"
            />
          </div>
          <button
            type="button"
            onClick={searchAddress}
            disabled={searchLoading || !searchQuery.trim()}
            className="px-4 py-2 text-xs font-bold uppercase tracking-wider rounded-lg brand-bg-primary text-white hover:opacity-90 transition disabled:opacity-50"
            data-testid="picker-search-btn"
          >
            {searchLoading ? <Loader2 size={14} className="animate-spin" /> : "Cari"}
          </button>
        </div>

        {/* Loading / Results / Empty / Error states */}
        {searchLoading && (
          <div className="mt-1 text-xs text-[#7A5440] flex items-center gap-1.5" data-testid="picker-search-loading">
            <Loader2 size={12} className="animate-spin" /> Mencari alamat...
          </div>
        )}

        {!searchLoading && searchError && (
          <div className="mt-1 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5" data-testid="picker-search-error">
            Gagal mencari alamat. Silakan coba lagi.
          </div>
        )}

        {!searchLoading && !searchError && searchTouched && Array.isArray(searchResults) && searchResults.length === 0 && (
          <div className="mt-1 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5" data-testid="picker-search-empty">
            Alamat tidak ditemukan. Coba gunakan nama jalan, gedung, atau area yang lebih spesifik.
          </div>
        )}

        {!searchLoading && Array.isArray(searchResults) && searchResults.length > 0 && (
          <div className="mt-1 max-h-56 overflow-y-auto bg-white border border-[#E5E5E5] rounded-lg shadow-sm divide-y divide-[#F0F0F0]" data-testid="picker-search-results">
            {searchResults.map((r, i) => (
              <button
                key={`${r.place_id || i}`}
                type="button"
                onClick={() => selectResult(r)}
                className="w-full text-left px-3 py-2 hover:brand-soft-bg transition flex items-start gap-2"
                data-testid={`picker-search-result-${i}`}
              >
                <MapPin size={14} className="brand-text-primary mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-bold text-[#1a1a1a] truncate">
                    {r.name || r.display_name?.split(",")[0] || "Lokasi"}
                  </div>
                  <div className="text-[11px] text-[#5C5C5C] truncate">
                    {r.display_name}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <div style={{ height }} className="rounded-lg overflow-hidden border border-[#E5E5E5]">
        <MapContainer
          center={[initialPos.lat, initialPos.lng]}
          zoom={14}
          style={{ height: "100%", width: "100%" }}
          scrollWheelZoom
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <Marker
            position={[pos.lat, pos.lng]}
            draggable
            ref={markerRef}
            eventHandlers={{
              dragend: () => {
                const m = markerRef.current;
                if (!m) return;
                const ll = m.getLatLng();
                updatePos({ lat: ll.lat, lng: ll.lng });
              },
            }}
          />
          <ClickHandler onPick={updatePos} />
          <CenterControl position={pos} zoomHint={searchZoomTick > 0 ? 16 : null} />
        </MapContainer>
      </div>

      <div className="flex items-center justify-between gap-2 flex-wrap">
        {showCoords ? (
          <div className="flex items-center gap-2 text-xs">
            <MapPin size={14} className="brand-text-primary" />
            <span className="font-mono text-[#1a1a1a] brand-soft-bg border border-[#FFD7A8] rounded px-2 py-0.5" data-testid="picker-coord-display">
              {pos.lat.toFixed(7)}, {pos.lng.toFixed(7)}
            </span>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-xs text-[#7A5440]">
            <MapPin size={14} className="brand-text-primary" />
            <span>Pin lokasi tersimpan</span>
          </div>
        )}
        <button
          type="button"
          onClick={useMyLocation}
          className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider px-3 py-1 rounded bg-[#FAF9F6] border border-[#E5E5E5] hover:brand-soft-bg transition"
          data-testid="picker-use-my-location"
        >
          <Crosshair size={12} /> Cari Lokasi Saya
        </button>
      </div>

      <div className="text-[11px] text-[#7A5440]">
        {showCoords
          ? <>Drag pin atau klik di peta untuk memilih lokasi tepat. Koordinat akan otomatis tersimpan dalam format <code>lat,lng</code>.</>
          : <>Drag pin atau klik di peta untuk memilih titik lokasi yang tepat.</>
        }
      </div>
      <div className="text-[11px] text-[#7A5440] italic" data-testid="picker-search-tip">
        Setelah memilih hasil pencarian, Anda masih dapat menggeser pin agar lebih akurat.
      </div>
    </div>
  );
}

export { parseCoord, formatCoord };
